import { LitElement, html } from "@lit"

class MobileMenuPanel extends LitElement {
  static properties = {
    isOpen: { type: Boolean, reflect: true, attribute: 'is-open' },
  }

  constructor() {
    super()
    this.isOpen = false
    this.focusableElements = []

    this._handleSubMenuOpen = this._handleSubMenuOpen.bind(this)
    this._handleSubMenuClose = this._handleSubMenuClose.bind(this)
    this._handleAccordionToggle = this._handleAccordionToggle.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.$container = this.querySelector(".mobile-menu__container")
    this.$landing = this.querySelector('.mobile-menu__landing')
    this.$top = this.querySelector('.mobile-menu__top')
    this.parentItems = this.querySelectorAll(".js-mobile-parent-item")
    this.subPanels = this.querySelectorAll(".mobile-menu__sub-panel")
    this.closeSubpanelBtn = this.querySelector(".js-submenu-close-button")

    for (const item of this.parentItems) {
      item.addEventListener("click", this._handleSubMenuOpen)
    }

    this.closeSubpanelBtn?.addEventListener("click", this._handleSubMenuClose)
    this.$container?.addEventListener('sl-show', this._handleAccordionToggle)
    this.$container?.addEventListener('sl-hide', this._handleAccordionToggle)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    for (const item of this.parentItems) {
      item.removeEventListener("click", this._handleSubMenuOpen)
    }

    this.closeSubpanelBtn?.removeEventListener("click", this._handleSubMenuClose)
    this.$container?.removeEventListener('sl-show', this._handleAccordionToggle)
    this.$container?.removeEventListener('sl-hide', this._handleAccordionToggle)
  }

  firstUpdated() {
    this.focusableElements = [
      ...this.querySelectorAll('a[href], button, sl-details, sl-button, [tabindex]')
    ]

    this.$landing = this.querySelector('.mobile-menu__landing')
    this.$top = this.querySelector('.mobile-menu__top')
    this._syncAccordionTriggers()

    if (this.isOpen) {
      this._updateElementFocusability('landing')
    } else {
      this._resetElementFocusability()
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('isOpen')) {
      if (this.isOpen) {
        this._updateElementFocusability('landing')
      } else {
        this._resetElementFocusability()
      }
    }
  }

  _setExpanded(trigger, expanded) {
    if (!trigger) return
    if (typeof toggleAriaExpanded === 'function') {
      toggleAriaExpanded(trigger, expanded)
      return
    }
    trigger.setAttribute('aria-expanded', String(expanded))
  }

  _syncAccordionTriggers() {
    for (const details of this.querySelectorAll('sl-details')) {
      this._setAccordionExpanded(details, Boolean(details.open))
    }
  }

  _setAccordionExpanded(details, expanded) {
    this._setExpanded(details, expanded)
    this._setExpanded(details.querySelector('[slot="summary"]'), expanded)
  }

  _handleAccordionToggle(event) {
    if (event.target?.localName !== 'sl-details') return
    this._setAccordionExpanded(event.target, event.type === 'sl-show')
  }

  _handleSubMenuOpen(e) {
    e.preventDefault()

    const targetId =
      e.currentTarget?.getAttribute('data-target') ||
      e.target?.getAttribute('data-target') ||
      ''
    if (targetId) {
      for (const item of this.subPanels) {
        const panelId = item.getAttribute('data-panel-id')
        if (panelId === targetId) {
          item.classList.remove('transparent')
          this._updateElementFocusability(item)
        } else {
          item.classList.add('transparent')
        }
      }
    }

    this.$container.classList.add('mobile-submenu__opened')
  }

  _handleSubMenuClose() {
    this.$container.classList.remove('mobile-submenu__opened')

    for (const item of this.subPanels) {
      item.classList.add('transparent')
    }

    this._updateElementFocusability('landing')
  }

  _isInLevel(element, level) {
    if (typeof level === 'string' && level === 'landing') {
      return !!element.closest('.mobile-menu__landing')
    }

    if (level instanceof HTMLElement) {
      return (
        !!element.closest(
          `[data-panel-id="${level.getAttribute('data-panel-id')}"]`,
        ) || !!element.closest('.mobile-menu__top')
      )
    }

    return false
  }

  _getFocusTarget(level) {
    if (typeof level === 'string' && level === 'landing') {
      return this.$landing?.querySelector('a[href], button:not([disabled])')
    }

    if (level instanceof HTMLElement) {
      return (
        this.closeSubpanelBtn ||
        level.querySelector('a[href], button:not([disabled])')
      )
    }

    return null
  }

  _shouldKeepFocusInDrawer() {
    const drawer = this.closest('sl-drawer')
    return Boolean(drawer?.hasAttribute('open') || drawer?.open)
  }

  _unlockLevel(level) {
    this.$container?.removeAttribute('inert')

    if (typeof level === 'string' && level === 'landing') {
      this.$landing?.removeAttribute('inert')
    } else if (level instanceof HTMLElement) {
      level.removeAttribute('inert')
      this.$top?.removeAttribute('inert')
    }

    for (const element of this.focusableElements) {
      if (!this._isInLevel(element, level)) continue
      element.setAttribute('tabindex', '0')
      element.removeAttribute('inert')
    }
  }

  _lockOtherLevels(level) {
    this.$landing?.toggleAttribute('inert', level !== 'landing')
    this.$top?.toggleAttribute('inert', level === 'landing')

    for (const panel of this.subPanels) {
      const isActive =
        level instanceof HTMLElement &&
        panel.getAttribute('data-panel-id') ===
          level.getAttribute('data-panel-id')
      panel.toggleAttribute('inert', !isActive)
    }

    for (const element of this.focusableElements) {
      this._setReachable(element, this._isInLevel(element, level))
    }
  }

  _refreshFocusTrap(elementToFocus) {
    const drawer = this.closest('#MobileNav, #mobile-menu, sl-drawer')
    if (drawer && typeof trapFocus === 'function') {
      trapFocus(drawer, elementToFocus || drawer)
    }
  }

  _setReachable(element, isFocusable) {
    if (!element) return

    element.setAttribute('tabindex', isFocusable ? '0' : '-1')
    if (isFocusable) element.removeAttribute('inert')
  }

  _updateElementFocusability(level) {
    this._unlockLevel(level)

    const focusTarget = this._getFocusTarget(level)
    if (this._shouldKeepFocusInDrawer()) {
      focusTarget?.focus()
    }

    this._lockOtherLevels(level)

    if (this._shouldKeepFocusInDrawer()) {
      this._refreshFocusTrap(document.activeElement)
    }
  }

  _resetElementFocusability() {
    this.$container?.setAttribute('inert', '')
    this.$landing?.setAttribute('inert', '')
    this.$top?.setAttribute('inert', '')

    for (const panel of this.subPanels || []) {
      panel.setAttribute('inert', '')
    }

    for (const element of this.focusableElements) {
      this._setReachable(element, false)
    }
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define("mobile-menu-panel", MobileMenuPanel)
